import { applySupplierInventoryDeltasToParts } from "./supplierInventoryDeltaUtils";
import { applySupplierPartConfirmationToParts } from "./supplierPartConfirmationUtils";
import { SupplierPart } from "./partsCalculationUtils";
import {
  fetchSupplierPartsInventoryFromTypesense,
  SupplierInventoryPartHit,
} from "./typesenseUtils";

/**
 * Builds rootPartNumber → net available qty using the same client pipeline as
 * AI Discovery: Typesense stock sheet → self-consumption deltas → part confirmation.
 * Missing → 0.
 */
export const buildInventoryAvailableByRootPartFromTypesense = async ({
  typesenseAdapter,
  collectionName,
  supplierId,
  rootPartNumbers,
  supplierInventoryDeltaQuantityMap,
  supplierPartConfirmationQuantityMap,
}: {
  typesenseAdapter: any;
  collectionName?: string;
  supplierId: string;
  rootPartNumbers: string[];
  supplierInventoryDeltaQuantityMap?: Map<string, number>;
  supplierPartConfirmationQuantityMap?: Map<string, number>;
}): Promise<Record<string, number>> => {
  const uniqueRoots = Array.from(
    new Set(
      (rootPartNumbers || [])
        .map((root) => String(root || "").trim())
        .filter(Boolean),
    ),
  );

  const inventoryByRoot: Record<string, number> = {};
  for (const root of uniqueRoots) {
    inventoryByRoot[root] = 0;
  }

  if (!supplierId || uniqueRoots.length === 0) {
    return inventoryByRoot;
  }

  const typesenseParts: SupplierInventoryPartHit[] =
    await fetchSupplierPartsInventoryFromTypesense({
      typesenseAdapter,
      collectionName,
      supplierId,
      rootPartNumbers: uniqueRoots,
    });

  const partsForDelta: SupplierPart[] = typesenseParts.map((part) => ({
    rootPartNumber: part.rootPartNumber,
    familyQuantity: part.familyQuantity,
    updatedAt: part.updatedAt,
    source: part.source,
  }));

  const deltaAdjustedParts = applySupplierInventoryDeltasToParts({
    supplierId,
    parts: partsForDelta,
    supplierInventoryDeltaQuantityMap,
  });
  const adjustedParts = applySupplierPartConfirmationToParts({
    supplierId,
    parts: deltaAdjustedParts,
    supplierPartConfirmationQuantityMap,
  });

  for (const part of adjustedParts) {
    if (!part.rootPartNumber) continue;
    inventoryByRoot[part.rootPartNumber] = Math.max(
      0,
      Number(part.familyQuantity) || 0,
    );
  }

  return inventoryByRoot;
};
